import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../components/Icon';
import { useUser } from '../../context/UserContext';
import { resolveLorRosterId } from '../../utils/lorIdentity';

function TeamSide({ label, teams, teamId, setTeamId, excludedIds = [], selected, toggle, faab, setFaab, locked = false, destination, setDestination, threeTeam = false }) {
  const team = teams.find(t => String(t.roster_id) === String(teamId));
  return (
    <section className="lor-ta-side lor-card">
      <label className="lor-ta-label">{label}</label>
      <select className="lor-ta-select" value={teamId} disabled={locked} onChange={e => setTeamId(e.target.value)}>
        <option value="">Choose a team</option>
        {teams.filter(t => !excludedIds.map(String).includes(String(t.roster_id))).map(t => (
          <option key={t.roster_id} value={t.roster_id}>{t.team_name}</option>
        ))}
      </select>
      {locked && <div className="lor-ta-locked">🔒 Locked to your logged-in LOR franchise</div>}
      {threeTeam && team && <label className="lor-ta-destination">SEND THIS PACKAGE TO<select value={destination || ''} onChange={e => setDestination(e.target.value)}><option value="">Choose recipient…</option>{teams.filter(t => [teamId, ...excludedIds].map(String).includes(String(t.roster_id)) && String(t.roster_id) !== String(teamId)).map(t => <option value={t.roster_id} key={t.roster_id}>{t.team_name}</option>)}</select></label>}
      {team && <div className="lor-ta-faab-control">
        <div><label htmlFor={`faab-${team.roster_id}`}>FAAB INCLUDED</label><small>${team.faab_remaining} currently available</small></div>
        <div className="lor-ta-faab-input"><span>$</span><input id={`faab-${team.roster_id}`} type="number" min="0" max={team.faab_remaining} step="1" value={faab} onChange={e => setFaab(e.target.value)} /></div>
      </div>}
      {team && <div className="lor-ta-roster">
        {team.players.map(player => (
          <button type="button" key={player.player_id}
            className={`lor-ta-player${selected.includes(player.player_id) ? ' selected' : ''}`}
            onClick={() => toggle(player.player_id)}>
            <span className="lor-ta-pos">{player.position}</span>
            <span className="lor-ta-player-main"><strong>{player.name}</strong><small>{player.team} · {player.on_bye ? `BYE · ` : ''}{player.weekly_projection} this week · {player.forward_projection} next 4 · {player.ros_projection} ROS</small></span>
            <span className="lor-ta-value">{player.value}</span>
          </button>
        ))}
      </div>}
    </section>
  );
}

function ResultSide({ side }) {
  const forwardDelta = side.forward_lineup_delta ?? side.lineup_delta;
  const changeNames = players => players.length ? players.map(p => p.name).join(', ') : 'None';
  const depthDirection = side.depth_delta > 0 ? 'improved' : side.depth_delta < 0 ? 'declined' : 'unchanged';
  const receivedIds = new Set(side.receives.filter(a => a.player_id).map(a => a.player_id));
  const startersIn = new Set(side.starting_lineup_changes.in.map(p => p.player_id));
  const depthIn = new Set(side.depth_changes.in.map(p => p.player_id));
  const movement = (player, area) => {
    if (area === 'starter' && startersIn.has(player.player_id)) return receivedIds.has(player.player_id) ? 'NEW STARTER' : 'PROMOTED';
    if (area === 'bench' && depthIn.has(player.player_id)) return receivedIds.has(player.player_id) ? 'NEW DEPTH' : 'DEMOTED';
    return '';
  };
  return <article className="lor-ta-result-side">
    <div className="lor-ta-team-grade"><h3>{side.team_name}</h3><div><strong>{side.grade}</strong><small>{side.grade_score}/100</small></div></div>
    <div className="lor-ta-result-number">{side.received_value}</div>
    <div className="lor-ta-result-label">value received</div>
    <div className={`lor-ta-net ${side.net_value >= 0 ? 'positive' : 'negative'}`}>{side.net_value >= 0 ? '+' : ''}{side.net_value} net value</div>
    <div className="lor-ta-lineup"><span>This week's lineup</span><strong>{side.lineup_delta >= 0 ? '+' : ''}{side.lineup_delta} pts</strong></div>
    <div className="lor-ta-lineup"><span>Bye-neutral outlook</span><strong>{forwardDelta >= 0 ? '+' : ''}{forwardDelta} pts</strong></div>
    <div className="lor-ta-lineup"><span>FAAB balance</span><strong>${side.faab.before} → ${side.faab.after}</strong></div>
    <p className="lor-ta-fit">{side.fit_summary}</p>
    <div className="lor-ta-impact-grid">
      <div className={`lor-ta-impact ${side.starting_lineup_changed ? 'changed' : 'unchanged'}`}>
        <strong>Best lineup {side.starting_lineup_changed ? 'changes' : 'unchanged'}</strong>
        {side.starting_lineup_changed && <small>IN: {changeNames(side.starting_lineup_changes.in)}<br />OUT: {changeNames(side.starting_lineup_changes.out)}</small>}
      </div>
      <div className={`lor-ta-impact ${depthDirection}`}>
        <strong>Depth {depthDirection}</strong>
        <small>{side.depth_delta >= 0 ? '+' : ''}{side.depth_delta} depth value<br />IN: {changeNames(side.depth_changes.in)}<br />OUT: {changeNames(side.depth_changes.out)}</small>
      </div>
    </div>
    <h4>Receives</h4>
    {side.receives.length ? side.receives.map((a, i) => <div className="lor-ta-asset" key={`${a.name}-${i}`}>
      <span>{a.name}{a.keeper_caliber ? ' ⭐' : ''}</span><b>{a.value}</b>
    </div>) : <div className="lor-ta-muted">No assets</div>}
    <h4>NEW BEST POSSIBLE LINEUP · {side.lineup_after} PTS</h4>
    <div className="lor-ta-best-lineup">{side.best_lineup_after.map(player => (
      <div className={`lor-ta-lineup-player${player.player_id === '0' ? ' empty' : ''}${movement(player, 'starter') ? ' changed' : ''}`} key={player.slot}>
        <span className="lor-ta-slot">{player.slot}</span>
        <span><strong>{player.name}</strong><small>{player.position}{player.team ? ` · ${player.team}` : ''}</small>{movement(player, 'starter') && <em>{movement(player, 'starter')}</em>}</span>
        <b>{player.projected}</b>
      </div>
    ))}</div>
    <h4>BEST BENCH AFTER TRADE · DEPTH {side.depth_after}</h4>
    <div className="lor-ta-best-lineup">{side.best_bench_after.length ? side.best_bench_after.map(player => (
      <div className={`lor-ta-lineup-player${movement(player, 'bench') ? ' changed' : ''}`} key={player.player_id}>
        <span className="lor-ta-slot">BENCH</span>
        <span><strong>{player.name}</strong><small>{player.position} · {player.team}</small>{movement(player, 'bench') && <em>{movement(player, 'bench')}</em>}</span>
        <b>{player.weekly_projection}</b>
      </div>
    )) : <div className="lor-ta-muted lor-ta-bench-empty">No bench players remain.</div>}</div>
  </article>;
}

export default function LORTradeAnalyzer({ api }) {
  const { currentUser, csrfToken, refreshCsrf } = useUser();
  const [teams, setTeams] = useState([]);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [c, setC] = useState('');
  const [playersA, setPlayersA] = useState([]);
  const [playersB, setPlayersB] = useState([]);
  const [playersC, setPlayersC] = useState([]);
  const [faabA, setFaabA] = useState('0');
  const [faabB, setFaabB] = useState('0');
  const [faabC, setFaabC] = useState('0');
  const [threeTeam, setThreeTeam] = useState(false);
  const [toA, setToA] = useState('');
  const [toB, setToB] = useState('');
  const [toC, setToC] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('analyzer');
  const [targets, setTargets] = useState(null);
  const ownRosterId = resolveLorRosterId(currentUser, teams);
  const lockOwnTeam = Boolean(!currentUser?.guest && ownRosterId);

  useEffect(() => {
    fetch(`${api}/api/lor/trade-analyzer/teams`).then(r => {
      if (!r.ok) throw new Error(); return r.json();
    }).then(d => setTeams(d.teams || [])).catch(() => setError('Could not load current LOR rosters.'));
  }, [api]);
  useEffect(() => {
    if (lockOwnTeam && String(a) !== String(ownRosterId)) setA(String(ownRosterId));
  }, [lockOwnTeam, ownRosterId, a]);
  useEffect(() => {
    if (!lockOwnTeam) return;
    fetch(`${api}/api/lor/trade-targets/${ownRosterId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setTargets).catch(() => setTargets({ suggestions: [], error: true }));
  }, [api, lockOwnTeam, ownRosterId]);
  const changeA = value => { setA(value); setPlayersA([]); setFaabA('0'); setResult(null); };
  const changeB = value => { setB(value); setPlayersB([]); setFaabB('0'); setResult(null); };
  const changeC = value => { setC(value); setPlayersC([]); setFaabC('0'); setResult(null); };

  useEffect(() => {
    if (!threeTeam || !a || !b || !c) return;
    setToA(current => [String(b), String(c)].includes(String(current)) ? String(current) : String(b));
    setToB(current => [String(a), String(c)].includes(String(current)) ? String(current) : String(c));
    setToC(current => [String(a), String(b)].includes(String(current)) ? String(current) : String(a));
  }, [threeTeam, a, b, c]);

  const packageA = playersA.length || Number(faabA) > 0;
  const packageB = playersB.length || Number(faabB) > 0;
  const packageC = playersC.length || Number(faabC) > 0;
  const canEvaluate = useMemo(() => a && b && (threeTeam
    ? c && packageA && packageB && packageC && toA && toB && toC
    : packageA || packageB), [a, b, c, threeTeam, packageA, packageB, packageC, toA, toB, toC]);
  const toggle = (setter, selected) => id => setter(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  async function evaluate() {
    setLoading(true); setError(''); setResult(null);
    try {
      const submit = token => fetch(`${api}/api/lor/trade-analyzer/evaluate`, {
        method: 'POST', credentials: 'include', headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify(threeTeam ? {participants: [
          {roster_id: Number(a), to_roster_id: Number(toA), players: playersA, faab: Number(faabA || 0)},
          {roster_id: Number(b), to_roster_id: Number(toB), players: playersB, faab: Number(faabB || 0)},
          {roster_id: Number(c), to_roster_id: Number(toC), players: playersC, faab: Number(faabC || 0)},
        ]} : {roster_id_a: Number(a), roster_id_b: Number(b), players_a: playersA, players_b: playersB, faab_a: Number(faabA || 0), faab_b: Number(faabB || 0)}),
      });
      let token = csrfToken || await refreshCsrf?.();
      let response = await submit(token || '');
      let data = await response.json();
      if (response.status === 403 && data.detail === 'CSRF validation failed') {
        token = await refreshCsrf?.();
        if (token) { response = await submit(token); data = await response.json(); }
      }
      if (!response.ok) throw new Error(data.detail || 'Analysis failed');
      setResult(data);
    } catch (err) { setError(err.message || 'Could not evaluate this trade.'); }
    finally { setLoading(false); }
  }
  function loadSuggestion(idea) {
    setA(String(ownRosterId)); setB(String(idea.partner_roster_id));
    setPlayersA(idea.suggested_offer ? [idea.suggested_offer.player_id] : []);
    setPlayersB([idea.target.player_id]); setFaabA('0'); setFaabB('0');
    setResult(null); setActiveTab('analyzer');
  }

  return <div className="lor-ta-page">
    <div className="lor-page-header">
      <div><h2 className="lor-page-title"><Icon emoji="🔁" size={18} /> Trade Analyzer</h2>
        <p className="lor-ta-subtitle">Two- or three-team trades with live values, roster fit and legal lineup impact.</p></div>
      <span className="lor-ta-live">● LIVE LOR DATA</span>
    </div>
    {lockOwnTeam && <div className="lor-ta-tabs">
      <button className={activeTab === 'analyzer' ? 'active' : ''} onClick={() => setActiveTab('analyzer')}>Trade Analyzer</button>
      <button className={activeTab === 'targets' ? 'active' : ''} onClick={() => setActiveTab('targets')}>Private Targets</button>
    </div>}
    {activeTab === 'targets' && <section className="lor-ta-targets">
      <div className="lor-ta-target-intro"><strong>{targets?.team_name || 'Your franchise'} trade desk</strong><span>Needs: {(targets?.needs || []).slice(0,2).join(', ') || 'loading'} · Leverage: {(targets?.surpluses || []).slice(0,2).join(', ') || 'loading'}</span><small>{targets?.privacy}</small></div>
      {(targets?.suggestions || []).map(idea => <article className="lor-ta-target" key={`${idea.partner_roster_id}-${idea.target.player_id}`}>
        <div><span>FIT {idea.fit_score}</span><strong>Target {idea.target.name}</strong><small>{idea.target.position} · {idea.partner} · {idea.target.weekly_projection} projected · value {idea.target.value}</small></div>
        <p>{idea.why}</p>
        <div className="lor-ta-target-offer"><span>Starting framework</span><b>{idea.suggested_offer ? `${idea.suggested_offer.name} → ${idea.target.name}` : `Ask about ${idea.target.name}`}</b><small>Your lineup: {idea.lineup_delta >= 0 ? '+' : ''}{idea.lineup_delta || 0} pts · Their lineup: {idea.partner_lineup_delta >= 0 ? '+' : ''}{idea.partner_lineup_delta || 0} pts</small></div>
        <button onClick={() => loadSuggestion(idea)}>Load in Analyzer</button>
      </article>)}
      {targets && !targets.suggestions?.length && <div className="lor-empty">No responsible one-for-one framework cleared the current roster-fit screen. Check again after the next roster move.</div>}
    </section>}
    {activeTab === 'analyzer' && <><div className="lor-ta-mode"><button className={!threeTeam ? 'active' : ''} onClick={() => { setThreeTeam(false); setResult(null); }}>2 Teams</button><button className={threeTeam ? 'active' : ''} onClick={() => { setThreeTeam(true); setResult(null); }}>+ Add Third Team</button></div>
    {threeTeam && <p className="lor-ta-flow-help">Choose each outgoing package and which selected franchise receives it.</p>}
    <div className={`lor-ta-grid${threeTeam ? ' three-team' : ''}`}>
      <TeamSide label={lockOwnTeam ? 'YOUR TEAM SENDS' : 'TEAM A SENDS'} teams={teams} teamId={a} setTeamId={changeA} excludedIds={[b, ...(threeTeam ? [c] : [])].filter(Boolean)} selected={playersA} toggle={toggle(setPlayersA, playersA)} faab={faabA} setFaab={setFaabA} locked={lockOwnTeam} destination={toA} setDestination={setToA} threeTeam={threeTeam} />
      <TeamSide label="TEAM B SENDS" teams={teams} teamId={b} setTeamId={changeB} excludedIds={[a, ...(threeTeam ? [c] : [])].filter(Boolean)} selected={playersB} toggle={toggle(setPlayersB, playersB)} faab={faabB} setFaab={setFaabB} destination={toB} setDestination={setToB} threeTeam={threeTeam} />
      {threeTeam && <TeamSide label="TEAM C SENDS" teams={teams} teamId={c} setTeamId={changeC} excludedIds={[a, b].filter(Boolean)} selected={playersC} toggle={toggle(setPlayersC, playersC)} faab={faabC} setFaab={setFaabC} destination={toC} setDestination={setToC} threeTeam />}
    </div>
    <button className="lor-ta-evaluate" disabled={!canEvaluate || loading} onClick={evaluate}>{loading ? 'Analyzing…' : 'Analyze Trade'}</button>
    {error && <div className="lor-ta-error">{error}</div>}
    {result && <section className="lor-ta-result lor-card">
      <div className="lor-ta-verdict"><span>{result.verdict}</span><strong>{result.edge_to ? `Edge: ${result.edge_to}` : 'Balanced exchange'}</strong><small>{result.value_gap_percent}% value gap</small></div>
      <div className="lor-ta-narrative"><strong>Why this trade does—or does not—make sense</strong><p>{result.narrative}</p></div>
      <div className={`lor-ta-results-grid${result.multi_team ? ' three-team' : ''}`}>{result.multi_team ? result.teams.map(side => <ResultSide side={side} key={side.roster_id} />) : <><ResultSide side={result.side_a} /><ResultSide side={result.side_b} /></>}</div>
      <div className="lor-ta-method"><strong>How this is graded:</strong> {result.methodology.note}<br />Market: {result.methodology.market_source} · Week {result.methodology.projection_week || 'current'} immediate impact · Weeks {(result.methodology.forward_projection_weeks || []).join(', ') || 'current'} near-term · Weeks {(result.methodology.ros_projection_weeks || []).join(', ') || 'current'} ROS.<br />FAAB market: {result.methodology.faab_market.value_per_dollar} value per $1 · ${result.methodology.faab_market.league_average_remaining} average remaining · waiver-market strength {result.methodology.faab_market.market_strength}.</div>
    </section>}</>}
  </div>;
}
